(() => {
  if (customElements.get('finch-photo-reviews')) return;

  class FinchPhotoReviews extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.section = this.closest('[data-fpr-section]');
      this.content = this.querySelector('[data-fpr-content]');
      this.track = this.content?.querySelector('[data-fpr-track]');
      this.previousButton = this.content?.querySelector('[data-fpr-previous]');
      this.nextButton = this.content?.querySelector('[data-fpr-next]');
      this.status = this.content?.querySelector('[data-fpr-status]');
      this.dialog = this.content?.querySelector('[data-fpr-dialog]');
      this.dialogImage = this.content?.querySelector('[data-fpr-dialog-image]');
      this.dialogStars = this.content?.querySelector('[data-fpr-dialog-stars]');
      this.dialogTitle = this.content?.querySelector('[data-fpr-dialog-title]');
      this.dialogBody = this.content?.querySelector('[data-fpr-dialog-body]');
      this.dialogReviewer = this.content?.querySelector('[data-fpr-dialog-reviewer]');
      this.dialogDate = this.content?.querySelector('[data-fpr-dialog-date]');
      this.dialogVariant = this.content?.querySelector('[data-fpr-dialog-variant]');
      this.dialogPrevious = this.content?.querySelector('[data-fpr-dialog-previous]');
      this.dialogNext = this.content?.querySelector('[data-fpr-dialog-next]');
      this.dialogCount = this.content?.querySelector('[data-fpr-dialog-count]');
      this.dialogThumbnails = this.content?.querySelector('[data-fpr-dialog-thumbnails]');
      this.items = [];
      this.dialogItem = null;
      this.dialogImageIndex = 0;
      this.judgeFeedState = { filterRating: null, sort: 'most-recent' };
      this.uploadInputState = new Map();
      this.abortController = new AbortController();

      if (
        !this.section ||
        !this.content ||
        !this.track ||
        !this.dataset.endpoint ||
        !this.dataset.shopDomain ||
        !this.dataset.productId
      ) {
        return;
      }

      this.bindEvents();
      this.startIntegration();
      this.load();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.integrationObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.integrationFrame) cancelAnimationFrame(this.integrationFrame);
      this.cleanupIntegration();
      delete this.dataset.initialized;
    }

    startIntegration() {
      const shopifySection = this.section.closest('.shopify-section');
      this.integrationRoot = shopifySection?.parentElement || document.body;
      this.integrationObserver = new MutationObserver(() => {
        this.queueIntegration();
      });
      this.integrationObserver.observe(this.integrationRoot, { childList: true, subtree: true });
      this.mountIntegration();
    }

    queueIntegration() {
      if (this.integrationFrame) return;
      this.integrationFrame = requestAnimationFrame(() => {
        this.integrationFrame = null;
        this.mountIntegration();
      });
    }

    mountIntegration() {
      const widget = this.findJudgeWidget();
      if (!widget) return;

      if (this.widget && this.widget !== widget) this.cleanupIntegration();

      this.widget = widget;
      this.widget.classList.add('fpr-is-integrated');
      this.widget.style.setProperty('--fpr-judge-star-color', this.dataset.starColor || '#f4b400');
      this.mountPhotoRail(widget);
      this.setupJudgeFeedCompatibility(widget);
      this.enhanceJudgeImageUpload(widget);
      this.syncMediaState();
    }

    enhanceJudgeImageUpload(widget) {
      const inputs = widget.querySelectorAll(
        ".jdgm-form-wrapper input[type='file'][name='pictures'], " +
          ".jdgm-form-dynamic-wrapper input[type='file'][name='pictures'], " +
          ".jdgm-form-wrapper input[type='file'][name='media'], " +
          ".jdgm-form-dynamic-wrapper input[type='file'][name='media']"
      );

      inputs.forEach((input, index) => {
        if (this.uploadInputState.has(input)) return;

        const formWrapper = input.closest('.jdgm-form-wrapper, .jdgm-form-dynamic-wrapper');
        if (!formWrapper) return;

        const title = formWrapper.querySelector(
          '.jdgm-picture-fieldset-title, .jdgm-picture-dynamic-fieldset-title'
        );
        this.uploadInputState.set(input, {
          accept: input.getAttribute('accept'),
          ariaDescribedBy: input.getAttribute('aria-describedby'),
          ariaLabel: input.getAttribute('aria-label'),
          multiple: input.hasAttribute('multiple'),
          title,
          titleText: title?.textContent || '',
        });

        input.accept = 'image/gif,image/jpeg,image/jpg,image/png,image/webp';
        input.multiple = true;
        input.setAttribute(
          'aria-label',
          this.dataset.uploadAriaLabel || 'Kies maximaal 5 afbeeldingen voor je beoordeling'
        );

        if (title && this.dataset.uploadLabel) title.textContent = this.dataset.uploadLabel;

        const fieldset = input.closest(
          '.jdgm-picture-fieldset, .jdgm-picture-dynamic-fieldset, .jdgm-form__fieldset'
        );
        if (!fieldset || fieldset.querySelector('[data-fpr-upload-help]')) return;

        const help = document.createElement('p');
        help.className = 'fpr__upload-help';
        help.dataset.fprUploadHelp = '';
        help.id = `FinchReviewUploadHelp-${this.dataset.productId}-${index}`;
        help.textContent =
          this.dataset.uploadHelp || 'Voeg maximaal 5 afbeeldingen toe. Maximaal 10 MB per afbeelding.';
        fieldset.append(help);
        const describedBy = new Set(
          (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)
        );
        describedBy.add(help.id);
        input.setAttribute('aria-describedby', [...describedBy].join(' '));
      });
    }

    restoreJudgeImageUpload() {
      this.uploadInputState?.forEach((state, input) => {
        const restoreAttribute = (name, value) => {
          if (value === null) input.removeAttribute(name);
          else input.setAttribute(name, value);
        };

        restoreAttribute('accept', state.accept);
        restoreAttribute('aria-describedby', state.ariaDescribedBy);
        restoreAttribute('aria-label', state.ariaLabel);
        input.toggleAttribute('multiple', state.multiple);
        if (state.title) state.title.textContent = state.titleText;
      });

      this.widget?.querySelectorAll('[data-fpr-upload-help]').forEach((help) => help.remove());
      this.uploadInputState?.clear();
    }

    findJudgeWidget() {
      const widgets = [...document.querySelectorAll('.jdgm-rev-widg')];
      if (!widgets.length) return null;

      const productId = String(this.dataset.productId);
      const productWidget = widgets.find((candidate) => {
        const owner = candidate.closest('.jdgm-review-widget, [data-product-id]');
        return String(candidate.dataset.productId || owner?.dataset.productId || '') === productId;
      });
      if (productWidget?.querySelector('.jdgm-rev-widg__header')) return productWidget;

      const hostSection = this.section.closest('.shopify-section');
      if (!hostSection) return null;

      const widget = widgets.find((candidate) => {
          const appSection = candidate.closest('.shopify-section');
          return (
            appSection &&
            Boolean(hostSection.compareDocumentPosition(appSection) & Node.DOCUMENT_POSITION_FOLLOWING)
          );
        });

      return widget?.querySelector('.jdgm-rev-widg__header') ? widget : null;
    }

    mountPhotoRail(widget) {
      this.content.classList.add('fpr-photo-rail');
      if (this.content.parentElement === widget) return;

      const insertionPoint = widget.querySelector(
        ':scope > .jdgm-rev-widg__body, :scope > .jdgm-rev-widg__paginate-spinner-wrapper'
      );
      widget.insertBefore(this.content, insertionPoint);
    }

    syncMediaState() {
      if (!this.widget) return;

      const hasCustomMedia = this.items.length > 0 && this.track.childElementCount > 0;
      this.content.hidden = !hasCustomMedia;
      this.widget.classList.toggle('fpr-has-photo-rail', hasCustomMedia);

      if (hasCustomMedia) this.queueControlsUpdate();
    }

    queueControlsUpdate() {
      requestAnimationFrame(() => this.updateControls());
    }

    cleanupIntegration() {
      this.removeJudgeFeedCompatibility();
      this.restoreJudgeImageUpload();
      this.widget?.classList.remove('fpr-is-integrated', 'fpr-has-photo-rail');
      this.widget?.style.removeProperty('--fpr-judge-star-color');
      if (this.content) {
        this.content.hidden = true;
        this.content.classList.remove('fpr-photo-rail');
        if (this.content.parentElement !== this) this.append(this.content);
      }
      this.widget = null;
    }

    setupJudgeFeedCompatibility(widget) {
      const productData = window.jdgm?.data?.reviewWidget?.[this.dataset.productId];
      if (!widget || !Array.isArray(productData?.reviews)) return;
      if (this.judgeFeedWidget === widget) return;

      this.removeJudgeFeedCompatibility();
      this.judgeFeedWidget = widget;
      this.judgeFeedClickHandler = (event) => this.handleJudgeFeedClick(event);
      this.judgeFeedChangeHandler = (event) => this.handleJudgeFeedChange(event);
      widget.addEventListener('click', this.judgeFeedClickHandler, true);
      widget.addEventListener('change', this.judgeFeedChangeHandler, true);
    }

    removeJudgeFeedCompatibility() {
      if (!this.judgeFeedWidget) return;
      this.judgeFeedWidget.removeEventListener('click', this.judgeFeedClickHandler, true);
      this.judgeFeedWidget.removeEventListener('change', this.judgeFeedChangeHandler, true);
      this.judgeFeedWidget = null;
      this.judgeFeedClickHandler = null;
      this.judgeFeedChangeHandler = null;
    }

    handleJudgeFeedClick(event) {
      const pageButton = event.target.closest('.jdgm-paginate__page');
      if (pageButton && this.judgeFeedWidget.contains(pageButton)) {
        const page = Number.parseInt(pageButton.dataset.page, 10);
        if (!Number.isInteger(page) || page < 1) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.loadJudgeFeed(page);
        return;
      }

      const histogramRow = event.target.closest('.jdgm-histogram__row');
      if (!histogramRow || !this.judgeFeedWidget.contains(histogramRow)) return;

      const rating = Number.parseInt(histogramRow.dataset.rating, 10);
      this.judgeFeedState.filterRating = Number.isInteger(rating) ? this.clamp(rating, 1, 5) : null;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.loadJudgeFeed(1);
    }

    handleJudgeFeedChange(event) {
      const select = event.target.closest('.jdgm-sort-dropdown');
      if (!select || !this.judgeFeedWidget.contains(select)) return;

      this.judgeFeedState.sort = select.value || 'most-recent';
      event.preventDefault();
      event.stopImmediatePropagation();
      this.loadJudgeFeed(1);
    }

    async loadJudgeFeed(page) {
      if (this.judgeFeedLoading || !this.judgeFeedWidget) return;

      const body = this.judgeFeedWidget.querySelector('.jdgm-rev-widg__body');
      const paginate = this.judgeFeedWidget.querySelector('.jdgm-paginate');
      const perPage = this.clamp(Number.parseInt(paginate?.dataset.perPage, 10) || 5, 1, 50);
      const endpoint = new URL(paginate?.dataset.url || this.dataset.endpoint);
      const sort = this.judgeSortParameters(this.judgeFeedState.sort);

      endpoint.searchParams.set('url', this.dataset.shopDomain);
      endpoint.searchParams.set('shop_domain', this.dataset.shopDomain);
      endpoint.searchParams.set('platform', 'shopify');
      endpoint.searchParams.set('product_id', this.dataset.productId);
      endpoint.searchParams.set('page', String(page));
      endpoint.searchParams.set('per_page', String(perPage));
      if (sort.by) endpoint.searchParams.set('sort_by', sort.by);
      if (sort.direction) endpoint.searchParams.set('sort_dir', sort.direction);
      if (this.judgeFeedState.filterRating) {
        endpoint.searchParams.set('filter_rating', String(this.judgeFeedState.filterRating));
      }

      this.judgeFeedLoading = true;
      body?.classList.add('fpr-judge-feed-loading');
      body?.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(endpoint.toString(), {
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(`Judge.me request failed with ${response.status}`);

        const data = await response.json();
        if (!Array.isArray(data?.reviews)) throw new Error('Judge.me returned an unsupported response.');
        this.renderJudgeFeed(data, page, perPage);
      } catch (error) {
        if (error?.name !== 'AbortError' && window.Shopify?.designMode) {
          console.warn('Judge.me reviews could not be updated.', error);
        }
      } finally {
        this.judgeFeedLoading = false;
        body?.classList.remove('fpr-judge-feed-loading');
        body?.removeAttribute('aria-busy');
      }
    }

    judgeSortParameters(value) {
      return (
        {
          'highest-rating': { by: 'rating', direction: 'desc' },
          'lowest-rating': { by: 'rating', direction: 'asc' },
          'with-pictures': { by: 'with_pictures', direction: '' },
          'pictures-first': { by: 'pictures_first', direction: '' },
          'videos-first': { by: 'videos_first', direction: '' },
          'most-helpful': { by: 'most_helpful', direction: '' },
          'most-recent': { by: 'created_at', direction: 'desc' },
        }[value] || { by: 'created_at', direction: 'desc' }
      );
    }

    renderJudgeFeed(data, requestedPage, perPage) {
      const reviewsContainer = this.judgeFeedWidget?.querySelector('.jdgm-rev-widg__reviews');
      const paginate = this.judgeFeedWidget?.querySelector('.jdgm-paginate');
      if (!reviewsContainer || !paginate) return;

      const reviews = document.createDocumentFragment();
      data.reviews.forEach((review) => reviews.append(this.createJudgeReview(review)));
      reviewsContainer.replaceChildren(reviews);

      const currentPage = this.clamp(
        Number.parseInt(data?.pagination?.current_page, 10) || requestedPage,
        1,
        Number.MAX_SAFE_INTEGER
      );
      const totalPages = Math.max(currentPage, Number.parseInt(data?.pagination?.total_pages, 10) || currentPage);
      this.renderJudgePagination(paginate, currentPage, totalPages, perPage);
      this.updateJudgeFilterState();

      const judge = window.jdgm;
      if (judge?.setupMediaGallery && judge?.['$']) {
        judge.setupMediaGallery(judge['$'](reviewsContainer));
      }

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      reviewsContainer.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }

    createJudgeReview(review) {
      const article = document.createElement('div');
      article.className = 'jdgm-rev jdgm-divider-top jdgm--done-setup';
      article.dataset.verifiedBuyer = review?.verified_buyer === true ? 'true' : 'false';
      article.dataset.reviewId = this.toText(review?.uuid);
      article.dataset.reviewLanguage = this.toText(review?.language);
      article.dataset.productTitle = this.toText(review?.product_title);
      article.dataset.productUrl = this.toText(review?.product_url);
      article.dataset.thumbUpCount = String(Number.parseInt(review?.thumb_up, 10) || 0);
      article.dataset.thumbDownCount = String(Number.parseInt(review?.thumb_down, 10) || 0);

      const header = document.createElement('div');
      header.className = 'jdgm-rev__header';

      const reviewerName = this.toText(review?.reviewer_name) || 'Klant';
      const icon = document.createElement('div');
      icon.className = 'jdgm-rev__icon';
      if (this.safeImageUrl(review?.avatar_image_url, 96)) {
        const avatar = document.createElement('img');
        avatar.className = 'jdgm-rev__avatar-image';
        avatar.src = this.safeImageUrl(review.avatar_image_url, 96);
        avatar.alt = '';
        avatar.loading = 'lazy';
        avatar.width = 48;
        avatar.height = 48;
        icon.append(avatar);
      } else {
        icon.textContent = (this.toText(review?.reviewer_initial) || reviewerName.charAt(0)).toUpperCase();
      }
      header.append(icon);

      const rating = this.clamp(Math.round(Number.parseFloat(review?.rating) || 0), 0, 5);
      const stars = document.createElement('span');
      stars.className = 'jdgm-rev__rating';
      stars.dataset.score = String(rating);
      stars.tabIndex = 0;
      stars.setAttribute('aria-label', `${rating} ${this.dataset.starsLabel || 'van 5 sterren'}`);
      stars.setAttribute('role', 'img');
      for (let index = 1; index <= 5; index += 1) {
        const star = document.createElement('span');
        star.className = `jdgm-star jdgm--${index <= rating ? 'on' : 'off'}`;
        stars.append(star);
      }
      header.append(stars);

      const timestamp = document.createElement('time');
      timestamp.className = 'jdgm-rev__timestamp';
      timestamp.dateTime = this.validDate(review?.created_at);
      timestamp.dataset.content = timestamp.dateTime;
      timestamp.textContent = this.formatDate(timestamp.dateTime);
      header.append(timestamp);

      const breakElement = document.createElement('div');
      breakElement.className = 'jdgm-rev__br';
      header.append(breakElement);

      if (review?.verified_buyer === true) {
        const badgeWrapper = document.createElement('span');
        badgeWrapper.className = 'jdgm-rev__buyer-badge-wrapper';
        const badge = document.createElement('span');
        badge.className = 'jdgm-rev__buyer-badge';
        badge.setAttribute('aria-label', window.jdgmSettings?.verified_badge_text || 'Geverifieerd');
        badgeWrapper.append(badge);
        header.append(badgeWrapper);
      }

      const authorWrapper = document.createElement('span');
      authorWrapper.className = 'jdgm-rev__author-wrapper';
      const author = document.createElement('span');
      author.className = 'jdgm-rev__author';
      author.textContent = reviewerName;
      authorWrapper.append(author);
      const location = this.toText(review?.location);
      if (location) {
        const locationElement = document.createElement('span');
        locationElement.className = 'jdgm-rev__location';
        locationElement.textContent = location;
        authorWrapper.append(locationElement);
      }
      header.append(authorWrapper);
      article.append(header);

      const content = document.createElement('div');
      content.className = 'jdgm-rev__content';
      const titleText = this.toText(review?.title);
      if (titleText) {
        const title = document.createElement('b');
        title.className = 'jdgm-rev__title';
        title.textContent = titleText;
        content.append(title);
      }

      const body = document.createElement('div');
      body.className = 'jdgm-rev__body';
      const paragraph = document.createElement('p');
      paragraph.textContent = this.htmlToText(review?.body_html);
      body.append(paragraph);
      content.append(body);

      const pictures = document.createElement('div');
      pictures.className = 'jdgm-rev__pics';
      const pictureUrls = Array.isArray(review?.pictures_urls) ? review.pictures_urls : [];
      pictureUrls.forEach((picture, index) => {
        const original = this.safeImageUrl(picture?.huge || picture?.original, 1200);
        const thumbnail = this.safeImageUrl(picture?.small || picture?.compact || picture?.original, 160);
        if (!original || !thumbnail) return;

        const link = document.createElement('a');
        link.className = 'jdgm-rev__pic-link';
        link.href = original;
        link.dataset.mfpSrc = original;
        link.target = '_blank';
        link.rel = 'nofollow noopener';
        link.setAttribute('aria-label', `${this.dataset.openLabel || 'Open foto'} ${index + 1}`);
        const image = document.createElement('img');
        image.className = 'jdgm-rev__pic-img';
        image.src = thumbnail;
        image.dataset.src = thumbnail;
        image.alt = `${this.dataset.openLabel || 'Klantfoto'} ${index + 1}`;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 160;
        image.height = 160;
        link.append(image);
        pictures.append(link);
      });
      content.append(pictures);

      const variant = this.toText(review?.product_variant_title);
      if (variant) {
        const variantElement = document.createElement('div');
        variantElement.className = 'jdgm-rev__product-variant';
        variantElement.textContent = `${this.dataset.variantLabel || 'Variant:'} ${variant}`;
        content.append(variantElement);
      }
      article.append(content);

      const reply = this.htmlToText(review?.reply_content);
      if (reply) {
        const replyElement = document.createElement('div');
        replyElement.className = 'jdgm-rev__reply';
        const replyLabel = document.createElement('strong');
        replyLabel.textContent = `${this.toText(review?.shop_reply_name) || 'Winkel'}:`;
        const replyBody = document.createElement('p');
        replyBody.textContent = reply;
        replyElement.append(replyLabel, replyBody);
        article.append(replyElement);
      }

      return article;
    }

    renderJudgePagination(container, currentPage, totalPages, perPage) {
      container.dataset.perPage = String(perPage);
      const fragment = document.createDocumentFragment();
      const firstPage = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
      const lastPage = Math.min(totalPages, firstPage + 2);

      if (currentPage > 1) {
        fragment.append(this.createJudgePageButton(currentPage - 1, 'jdgm-paginate__prev-page'));
      }
      for (let page = firstPage; page <= lastPage; page += 1) {
        fragment.append(this.createJudgePageButton(page, page === currentPage ? 'jdgm-curt' : ''));
      }
      if (currentPage < totalPages) {
        fragment.append(this.createJudgePageButton(currentPage + 1, 'jdgm-paginate__next-page'));
      }
      container.replaceChildren(fragment);
    }

    createJudgePageButton(page, extraClass) {
      const button = document.createElement('a');
      button.className = ['jdgm-paginate__page', extraClass].filter(Boolean).join(' ');
      button.dataset.page = String(page);
      button.tabIndex = 0;
      button.setAttribute('role', 'button');
      button.setAttribute('aria-label', `Pagina ${page}`);
      if (extraClass === 'jdgm-curt') button.setAttribute('aria-current', 'page');
      if (!extraClass.includes('page')) button.textContent = String(page);
      return button;
    }

    updateJudgeFilterState() {
      this.judgeFeedWidget?.querySelectorAll('.jdgm-histogram__row').forEach((row) => {
        const rating = Number.parseInt(row.dataset.rating, 10);
        row.classList.toggle(
          'jdgm-histogram__row--selected',
          Number.isInteger(rating) && rating === this.judgeFeedState.filterRating
        );
      });
      const clear = this.judgeFeedWidget?.querySelector('.jdgm-histogram__clear-filter');
      if (clear) clear.hidden = !this.judgeFeedState.filterRating;
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.track.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-fpr-open]');
        if (!trigger) return;

        const index = Number.parseInt(trigger.dataset.fprOpen, 10);
        if (Number.isInteger(index)) this.openDialog(index);
      }, { signal });

      this.track.addEventListener(
        'scroll',
        () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.updateControls();
          });
        },
        { passive: true, signal }
      );

      this.previousButton?.addEventListener('click', () => this.scrollTrack(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.scrollTrack(1), { signal });
      this.dialogPrevious?.addEventListener('click', () => this.moveDialogImage(-1), { signal });
      this.dialogNext?.addEventListener('click', () => this.moveDialogImage(1), { signal });

      this.dialogThumbnails?.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-fpr-dialog-thumbnail]');
        if (!trigger) return;

        const index = Number.parseInt(trigger.dataset.fprDialogThumbnail, 10);
        if (Number.isInteger(index)) {
          this.dialogImageIndex = index;
          this.renderDialogImage();
        }
      }, { signal });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      }, { signal });

      this.dialog?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.moveDialogImage(-1);
        if (event.key === 'ArrowRight') this.moveDialogImage(1);
      }, { signal });

      this.dialog?.addEventListener('close', () => this.resetDialog(), { signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.track);
      }
    }

    async load() {
      const limit = this.clamp(Number.parseInt(this.dataset.limit, 10) || 20, 1, 40);
      const maxPages = this.clamp(Number.parseInt(this.dataset.maxPages, 10) || 5, 1, 8);
      const uniqueReviews = new Map();
      let currentPage = 1;
      let totalPages = maxPages;

      try {
        while (currentPage <= maxPages && currentPage <= totalPages && uniqueReviews.size < limit) {
          let data;

          try {
            data = await this.fetchReviewPage(currentPage);
          } catch (error) {
            if (error?.name === 'AbortError' || uniqueReviews.size === 0) throw error;
            break;
          }

          totalPages = Math.min(
            maxPages,
            this.clamp(Number.parseInt(data?.pagination?.total_pages, 10) || maxPages, 1, maxPages)
          );

          this.normalizeReviews(data?.reviews).forEach((review) => {
            if (!uniqueReviews.has(review.key)) uniqueReviews.set(review.key, review);
          });

          currentPage += 1;
        }

        this.items = [...uniqueReviews.values()].slice(0, limit);
        if (!this.items.length) {
          this.mountIntegration();
          return;
        }

        this.render();
        this.mountIntegration();

        if (this.status) {
          this.status.textContent = `${this.items.length} ${this.dataset.loadedLabel || ''}`.trim();
        }
      } catch (error) {
        if (error?.name !== 'AbortError' && window.Shopify?.designMode) {
          console.warn('Finch photo reviews could not be loaded.', error);
        }
      }
    }

    async fetchReviewPage(page) {
      const endpoint = new URL(this.dataset.endpoint);
      endpoint.searchParams.set('shop_domain', this.dataset.shopDomain);
      endpoint.searchParams.set('platform', 'shopify');
      endpoint.searchParams.set('product_id', this.dataset.productId);
      endpoint.searchParams.set('page', String(page));
      endpoint.searchParams.set('per_page', '30');

      const response = await fetch(endpoint.toString(), {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error(`Judge.me request failed with ${response.status}`);
      return response.json();
    }

    normalizeReviews(reviews) {
      if (!Array.isArray(reviews)) return [];

      return reviews.flatMap((review) => {
        const pictures = Array.isArray(review?.pictures_urls) ? review.pictures_urls : [];
        const reviewer = this.toText(review?.reviewer_name) || 'Klant';
        const title = this.toText(review?.title);
        const imageUrls = new Set();
        const images = pictures.flatMap((picture, index) => {
          const source = picture?.huge || picture?.original || picture?.compact || picture?.small;
          const large = this.safeImageUrl(source, 1200);
          if (!large || imageUrls.has(large)) return [];

          imageUrls.add(large);
          return [
            {
              alt: title || `${this.dataset.openLabel || 'Klantfoto'} ${reviewer} ${index + 1}`,
              large,
              medium: this.safeImageUrl(source, 640),
              small: this.safeImageUrl(source, 320),
            },
          ];
        });

        if (!images.length) return [];

        const body = this.htmlToText(review?.body_html);
        const createdAt = this.validDate(review?.created_at);
        const key =
          this.toText(review?.uuid) ||
          [reviewer, createdAt, body, images[0].large].filter(Boolean).join('|');

        return [
          {
            body,
            createdAt,
            images,
            key,
            rating: this.clamp(Math.round(Number.parseFloat(review?.rating) || 0), 0, 5),
            reviewer,
            title,
            variant: this.toText(review?.product_variant_title),
            verified: review?.verified_buyer === true,
          },
        ];
      });
    }

    render() {
      const fragment = document.createDocumentFragment();
      const imageSizes = this.getImageSizes();

      this.items.forEach((item, index) => {
        const card = document.createElement('li');
        card.className = ['fpr__card', this.dataset.cardClass || ''].filter(Boolean).join(' ');

        const article = document.createElement('article');
        article.className = 'fpr__article';

        const button = document.createElement('button');
        button.className = 'fpr__media-button';
        button.type = 'button';
        button.dataset.fprOpen = String(index);
        button.setAttribute(
          'aria-label',
          `${this.dataset.openLabel || 'Foto openen'} ${index + 1}: ${item.reviewer}, ${item.images.length} ${
            this.dataset.imagesLabel || "foto's"
          }`
        );

        button.append(this.createImage(item.images[0], imageSizes));

        if (item.images.length > 1) {
          const count = document.createElement('span');
          count.className = 'fpr__image-count';
          count.textContent = `+${item.images.length - 1}`;
          count.setAttribute('aria-label', `${item.images.length} ${this.dataset.imagesLabel || "foto's"}`);
          button.append(count);
        }

        article.append(button);

        card.append(article);
        fragment.append(card);
      });

      this.track.replaceChildren(fragment);
    }

    createImage(imageData, sizes) {
      const image = document.createElement('img');
      image.className = 'fpr__image';
      image.src = imageData.medium;
      image.srcset = `${imageData.small} 320w, ${imageData.medium} 640w, ${imageData.large} 1200w`;
      image.sizes = sizes;
      image.alt = imageData.alt;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 640;
      image.height = 640;
      return image;
    }

    getImageSizes() {
      const desktopColumns = this.clamp(Number.parseInt(this.dataset.desktopColumns, 10) || 5, 2, 6);
      const tabletColumns = this.clamp(Number.parseInt(this.dataset.tabletColumns, 10) || 3, 2, 4);
      const mobileColumns = this.clamp(Number.parseInt(this.dataset.mobileColumns, 10) || 2, 1, 3);

      return `(min-width: 990px) calc(min(34vw, 36rem) / ${desktopColumns}), (min-width: 750px) calc((100vw - 6.4rem) / ${tabletColumns}), calc((100vw - 3.2rem) / ${mobileColumns})`;
    }

    createStars(rating, className) {
      const stars = document.createElement('span');
      stars.className = className;
      stars.setAttribute('aria-label', `${rating} ${this.dataset.starsLabel || 'van 5 sterren'}`);

      for (let index = 1; index <= 5; index += 1) {
        const star = document.createElement('span');
        star.className = `fpr__star${index <= rating ? ' is-active' : ''}`;
        star.setAttribute('aria-hidden', 'true');
        star.textContent = '★';
        stars.append(star);
      }

      return stars;
    }

    openDialog(index) {
      const item = this.items[index];
      if (!item || !this.dialog || !this.dialogImage) return;

      if (typeof this.dialog.showModal !== 'function') {
        window.open(item.images[0].large, '_blank', 'noopener');
        return;
      }

      this.dialogItem = item;
      this.dialogImageIndex = 0;

      if (this.dialogStars) {
        this.dialogStars.replaceChildren(this.createStars(item.rating, 'fpr__dialog-stars-list'));
        this.dialogStars.hidden = !this.isEnabled('showRating') || item.rating === 0;
      }

      this.setOptionalText(this.dialogTitle, item.title, this.isEnabled('showTitle'));
      this.setOptionalText(this.dialogBody, item.body, Boolean(item.body));
      this.setOptionalText(
        this.dialogReviewer,
        `${this.dataset.reviewByLabel || ''} ${item.reviewer}${
          item.verified && this.isEnabled('showVerified') ? ` · ✓ ${this.dataset.verifiedLabel || 'Geverifieerd'}` : ''
        }`.trim(),
        this.isEnabled('showReviewer') && Boolean(item.reviewer)
      );
      this.setOptionalText(
        this.dialogDate,
        this.formatDate(item.createdAt),
        this.isEnabled('showDate') && Boolean(item.createdAt)
      );
      this.setOptionalText(
        this.dialogVariant,
        `${this.dataset.variantLabel || 'Variant:'} ${item.variant}`,
        this.isEnabled('showVariant') && Boolean(item.variant)
      );

      this.renderDialogThumbnails();
      this.renderDialogImage();
      this.dialog.showModal();
    }

    renderDialogImage() {
      const imageData = this.dialogItem?.images?.[this.dialogImageIndex];
      if (!imageData || !this.dialogImage) return;

      this.dialogImage.src = imageData.large;
      this.dialogImage.srcset = `${imageData.medium} 640w, ${imageData.large} 1200w`;
      this.dialogImage.sizes = '(min-width: 750px) 70vw, 100vw';
      this.dialogImage.alt = imageData.alt;

      const hasMultipleImages = this.dialogItem.images.length > 1;
      if (this.dialogPrevious) this.dialogPrevious.hidden = !hasMultipleImages;
      if (this.dialogNext) this.dialogNext.hidden = !hasMultipleImages;

      if (this.dialogCount) {
        this.dialogCount.hidden = !hasMultipleImages;
        this.dialogCount.textContent = `${this.dialogImageIndex + 1} / ${this.dialogItem.images.length}`;
      }

      this.dialogThumbnails?.querySelectorAll('[data-fpr-dialog-thumbnail]').forEach((thumbnail, index) => {
        const active = index === this.dialogImageIndex;
        thumbnail.classList.toggle('is-active', active);
        if (active) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
    }

    renderDialogThumbnails() {
      if (!this.dialogThumbnails || !this.dialogItem) return;

      const hasMultipleImages = this.dialogItem.images.length > 1;
      this.dialogThumbnails.hidden = !hasMultipleImages;
      if (!hasMultipleImages) {
        this.dialogThumbnails.replaceChildren();
        return;
      }

      const fragment = document.createDocumentFragment();
      this.dialogItem.images.forEach((imageData, index) => {
        const button = document.createElement('button');
        button.className = 'fpr__dialog-thumbnail';
        button.type = 'button';
        button.dataset.fprDialogThumbnail = String(index);
        button.setAttribute('aria-label', `${this.dataset.thumbnailLabel || 'Toon foto'} ${index + 1}`);
        const image = document.createElement('img');
        image.src = imageData.small;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 72;
        image.height = 72;
        button.append(image);
        fragment.append(button);
      });

      this.dialogThumbnails.replaceChildren(fragment);
    }

    moveDialogImage(direction) {
      const imageCount = this.dialogItem?.images?.length || 0;
      if (imageCount < 2) return;

      this.dialogImageIndex = (this.dialogImageIndex + direction + imageCount) % imageCount;
      this.renderDialogImage();
    }

    resetDialog() {
      if (this.dialogImage) {
        this.dialogImage.removeAttribute('src');
        this.dialogImage.removeAttribute('srcset');
      }
      this.dialogThumbnails?.replaceChildren();
      this.dialogItem = null;
      this.dialogImageIndex = 0;
    }

    setOptionalText(element, value, show) {
      if (!element) return;
      element.textContent = value || '';
      element.hidden = !show || !value;
    }

    scrollTrack(direction) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        behavior: reduceMotion ? 'auto' : 'smooth',
        left: direction * Math.max(this.track.clientWidth * 0.82, 240),
      });
    }

    updateControls() {
      if (!this.previousButton || !this.nextButton) return;

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.previousButton.disabled = this.track.scrollLeft <= 2;
      this.nextButton.disabled = maxScroll <= 2 || this.track.scrollLeft >= maxScroll - 2;
    }

    safeImageUrl(value, width) {
      if (typeof value !== 'string' || !value) return '';

      try {
        const url = new URL(value, window.location.origin);
        if (url.protocol !== 'https:') return '';
        url.searchParams.set('quality', '80');
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (_error) {
        return '';
      }
    }

    formatDate(value) {
      if (!value) return '';
      try {
        return new Intl.DateTimeFormat(this.dataset.locale || document.documentElement.lang || 'nl-NL', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }).format(new Date(value));
      } catch (_error) {
        return '';
      }
    }

    validDate(value) {
      if (typeof value !== 'string' || !value) return '';
      return Number.isNaN(Date.parse(value)) ? '' : value;
    }

    htmlToText(value) {
      if (typeof value !== 'string' || !value) return '';
      const template = document.createElement('template');
      template.innerHTML = value;
      return template.content.textContent.replace(/\s+/g, ' ').trim();
    }

    toText(value) {
      return typeof value === 'string' ? value.trim() : '';
    }

    isEnabled(key) {
      return this.dataset[key] === 'true';
    }

    clamp(value, minimum, maximum) {
      return Math.min(Math.max(value, minimum), maximum);
    }
  }

  customElements.define('finch-photo-reviews', FinchPhotoReviews);
})();
